import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import DashboardTabs from '@components/common/Dashboard/DashboardTabs';
import AIDimensions from '@src/components/ai-cohorts';
import { isConfigLoadingAtom, totalCombinationsAtom } from '@src/components/ai-cohorts/atoms';
import { configuredDimensionCount } from '@src/components/ai-cohorts/utils';
import { useFetchConfigList } from '@src/components/ai-cohorts/hooks/useFetchConfigList';
import ConfigChangeLogs from '@src/components/ai-cohorts/ConfigChangelogs';
import AdminOnly from '@src/components/common/AdminOnly';
import { aiAlertsCustomDimensionsUpperLimitSelector } from '@store/common/selectors';
import { VSI_AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT } from '@src/utils/aiAlerts/mapping';
import { isEiModule } from '@src/utils/constants';
import { Button, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  CohortWrapper,
  CohortHeader,
  CohortHeaderTitle,
  TabContainer,
  MainContainer,
  CustomDimensionInfo,
} from './styled';

const { baseUrl } = window.conviva;

export default function AIAlertCohorts() {
  const setLoading = useSetAtom(isConfigLoadingAtom);
  const setTotalComb = useSetAtom(totalCombinationsAtom);

  const aiAlertsCustomDimensionsUpperLimitFF = useSelector(
    aiAlertsCustomDimensionsUpperLimitSelector,
  );
  const maxCount =
    aiAlertsCustomDimensionsUpperLimitFF + VSI_AI_ALERTS_CUSTOM_DIMENSIONS_UPPER_LIMIT;
  const navigate = useNavigate();
  const [showLog, setShowLog] = useState(false);

  const { data, isFetching, isLoading } = useFetchConfigList();
  const { aiAlertsConfig = [] } = data ?? {
    aiAlertsConfig: [],
    customerRootCauseType: null,
  };

  useEffect(() => {
    setLoading(isFetching);
    if (!isFetching && aiAlertsConfig.length) {
      const { totalCombinations = 0 } = aiAlertsConfig[0];
      setTotalComb(totalCombinations);
    }
  }, [isFetching, aiAlertsConfig, setTotalComb, setLoading]);

  const handleViewSummaryClick = () => {
    navigate(`${baseUrl}/dashboard/ai-alerts`);
  };

  const currentConfiguredDimensionCount = configuredDimensionCount(aiAlertsConfig);

  return (
    <CohortWrapper>
      <TabContainer>
        <DashboardTabs />
      </TabContainer>
      <CohortHeader>
        <CohortHeaderTitle>
          AI Cohorts
          <Tooltip
            placement="topLeft"
            title="AI Cohorts shows the real-time data for number of combinations being scanned by AI Alerts and refreshes every 5 minutes."
          >
            <InfoCircleOutlined
              style={{ fontSize: '14px', marginLeft: '6px', marginRight: '6px' }}
            />
          </Tooltip>
          <AdminOnly />
          {isEiModule ? (
            <CustomDimensionInfo>
              Total Dimension{currentConfiguredDimensionCount < 2 ? '' : 's'} configured:
              {currentConfiguredDimensionCount} (Limit: {maxCount})
            </CustomDimensionInfo>
          ) : null}
        </CohortHeaderTitle>
        <span>
          <Button onClick={handleViewSummaryClick}>View AI Alerts Summary</Button>
          {isEiModule && (
            <Button
              style={{ marginLeft: '8px' }}
              onClick={() => {
                setShowLog(true);
              }}
            >
              Change Log
            </Button>
          )}
          {showLog && (
            <ConfigChangeLogs
              isOpen={showLog}
              toggleOpen={() => {
                setShowLog(false);
              }}
            />
          )}
        </span>
      </CohortHeader>
      <MainContainer>
        <AIDimensions isLoading={isLoading} isFetching={isFetching} data={data} />
      </MainContainer>
    </CohortWrapper>
  );
}
